import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDrugById } from '../data/drugs';
import { useDrugs } from '../contexts/DrugsContext';
import { Drug } from '../types';

export interface ConsumptionEntry {
  id: string;
  drugId: string;
  dosage: number;
  unit: string;
  timestamp: number;
  notes?: string;
}

interface ConsumptionContextType {
  entries: ConsumptionEntry[];
  addEntry: (entry: Omit<ConsumptionEntry, 'id' | 'timestamp'>) => void;
  removeEntry: (id: string) => void;
  clearEntries: () => void;
  getTimeSince: (timestamp: number) => { hours: number; minutes: number; seconds: number };
  getTimeUntilPeak: (entry: ConsumptionEntry) => number | null;
  getRemainingDuration: (entry: ConsumptionEntry) => number | null;
}

const ConsumptionContext = createContext<ConsumptionContextType | null>(null);

// This is a wrapper component to provide drugs context to the consumption context
export const ConsumptionProviderWithDrugs: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ConsumptionProvider>{children}</ConsumptionProvider>
  );
};

export const ConsumptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<ConsumptionEntry[]>(() => {
    try {
      // Load from session storage if available
      const saved = localStorage.getItem('consumption-entries');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading entries:', error);
      return [];
    }
  });

  // Save to session storage whenever entries change
  useEffect(() => {
    localStorage.setItem('consumption-entries', JSON.stringify(entries));
  }, [entries]);

  const addEntry = (entry: Omit<ConsumptionEntry, 'id' | 'timestamp'>) => {
    const newEntry = {
      ...entry,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
    };
    setEntries((prev) => [...prev, newEntry]);
  };

  const removeEntry = (id: string) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const clearEntries = () => {
    setEntries([]);
  };

  const getTimeSince = (timestamp: number) => {
    const diff = Math.max(0, Date.now() - timestamp);
    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  };

  // These functions now access drugs from the component that calls them
  const getTimeUntilPeak = (entry: ConsumptionEntry) => {
    // The drug lookup will be done in the component that calls this
    return null;
  };

  const getRemainingDuration = (entry: ConsumptionEntry) => {
    // The drug lookup will be done in the component that calls this
    return null;
  };

  return (
    <ConsumptionContext.Provider
      value={{
        entries,
        addEntry,
        removeEntry,
        clearEntries,
        getTimeSince,
        getTimeUntilPeak,
        getRemainingDuration,
      }}
    >
      {children}
    </ConsumptionContext.Provider>
  );
};

// Wrapper for the hooks to include drug lookups
export const useConsumptionLogWithDrugs = () => {
  const consumptionLog = useConsumptionLog();
  const { drugs } = useDrugs();
  
  return {
    ...consumptionLog,
    getTimeUntilPeak: (entry: ConsumptionEntry) => {
      const drug = getDrugById(entry.drugId, drugs);
      if (!drug || !drug.peakTime) return null;
      
      const elapsedMinutes = (Date.now() - entry.timestamp) / 60000;
      const remainingMinutes = drug.peakTime - elapsedMinutes;
      
      return remainingMinutes > 0 ? Math.floor(remainingMinutes) : 0;
    },
    
    getRemainingDuration: (entry: ConsumptionEntry) => {
      const drug = getDrugById(entry.drugId, drugs);
      if (!drug || !drug.duration) return null;
      
      const elapsedMinutes = (Date.now() - entry.timestamp) / 60000;
      const remainingMinutes = drug.duration - elapsedMinutes;
      
      return remainingMinutes > 0 ? Math.floor(remainingMinutes) : 0;
    }
  };
};

export const useConsumptionLog = () => {
  const context = useContext(ConsumptionContext);
  if (!context) {
    throw new Error('useConsumptionLog must be used within a ConsumptionProvider');
  }
  return context;
}; 